/**
 * SIGNED-IN DEVICES (Phase 6B · Phase 6 visual implementation)
 * ============================================================
 *
 * The devices a member is signed in on, and the things they can do about it: rename one, end one,
 * or end all the others.
 *
 * Nothing here is cosmetic. Each row is a real session that can really be revoked — a device that
 * disappears from this list stops being able to refresh. Ended sessions are deleted rather than
 * kept, so the list is the live picture and never a history.
 *
 * Phase 6 brought the Current Device / Other Devices hierarchy, the approved card treatment and
 * the approved typography. The session contract, the endpoints and every action are unchanged.
 * No device fingerprinting, no IP or location, no new session field.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Laptop, Loader2, Lock, LogOut, Pencil, Smartphone, X } from 'lucide-react';
import axios from 'axios';
import { memberTimeOptions } from '../utils/memberTime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  en: {
    subtitle: 'Where you are signed in right now. Ending a device signs it out.',
    currentDevice: 'CURRENT DEVICE',
    otherDevices: 'OTHER DEVICES',
    devices: (n) => `${n} ${n === 1 ? 'DEVICE' : 'DEVICES'}`,
    thisDevice: 'This device',
    activeNow: 'Active now',
    signedIn: 'Signed in',
    lastActive: 'Last active',
    end: 'End access',
    endOthers: 'Sign Out All Other Devices',
    ending: 'Ending…',
    only: 'This is the only device you are signed in on.',
    rename: 'Rename',
    save: 'Save',
    failed: 'That did not work. Please try again.',
    method: { password: 'with your password', google: 'with Google', apple: 'with Apple' },
    empty: 'No devices to show yet.',
    security: 'For your security, we only show the devices that are currently signed in to your account.',
  },
  es: {
    subtitle: 'Dónde tienes sesión abierta ahora. Al finalizar un dispositivo, se cierra su sesión.',
    currentDevice: 'DISPOSITIVO ACTUAL',
    otherDevices: 'OTROS DISPOSITIVOS',
    devices: (n) => `${n} ${n === 1 ? 'DISPOSITIVO' : 'DISPOSITIVOS'}`,
    thisDevice: 'Este dispositivo',
    activeNow: 'Activo ahora',
    signedIn: 'Sesión iniciada',
    lastActive: 'Última actividad',
    end: 'Finalizar acceso',
    endOthers: 'Cerrar sesión en los demás dispositivos',
    ending: 'Finalizando…',
    only: 'Este es el único dispositivo con sesión abierta.',
    rename: 'Renombrar',
    save: 'Guardar',
    failed: 'No se pudo. Por favor inténtalo de nuevo.',
    method: { password: 'con tu contraseña', google: 'con Google', apple: 'con Apple' },
    empty: 'Aún no hay dispositivos para mostrar.',
    security: 'Por tu seguridad, solo mostramos los dispositivos con sesión abierta en tu cuenta.',
  },
};

const when = (iso, lang) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-GB',
      memberTimeOptions({ day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }));
  } catch {
    return '';
  }
};

// Presentation only: the glyph follows the name the session already carries. No new field is
// stored, read or inferred about the device itself.
const glyphFor = (label = '') =>
  /mac|book|windows|linux|desktop|pc\b/i.test(label) ? Laptop : Smartphone;

export const SignedInDevices = ({ lang = 'en', onCurrentDeviceEnded }) => {
  const t = COPY[lang] || COPY.en;
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [renaming, setRenaming] = useState(null);
  const [draft, setDraft] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/sessions`);
      setSessions(res.data?.sessions || []);
    } catch (err) {
      setError(t.failed);
    } finally {
      setLoading(false);
    }
  }, [t.failed]);

  useEffect(() => { load(); }, [load]);

  const end = async (session) => {
    setBusy(session.session_id);
    setError('');
    try {
      const res = await axios.delete(`${API}/auth/sessions/${session.session_id}`);
      if (res.data?.was_current_device && onCurrentDeviceEnded) {
        onCurrentDeviceEnded();
        return;
      }
      await load();
    } catch (err) {
      setError(t.failed);
    } finally {
      setBusy('');
    }
  };

  const endOthers = async () => {
    setBusy('others');
    setError('');
    try {
      await axios.post(`${API}/auth/sessions/revoke-others`);
      await load();
    } catch (err) {
      setError(t.failed);
    } finally {
      setBusy('');
    }
  };

  const rename = async (session) => {
    setBusy(session.session_id);
    try {
      await axios.put(`${API}/auth/sessions/${session.session_id}/label`, { label: draft });
      setRenaming(null);
      await load();
    } catch (err) {
      setError(t.failed);
    } finally {
      setBusy('');
    }
  };

  if (loading) {
    return (
      <div className="devices__loading" data-testid="devices-loading">
        <Loader2 size={18} className="animate-spin" />
      </div>
    );
  }

  const current = sessions.filter((s) => s.is_current);
  const others = sessions.filter((s) => !s.is_current);

  const Row = ({ s, index }) => {
    const Glyph = glyphFor(s.label);
    const isRenaming = renaming === s.session_id;
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.04 * index }}
        className="devices__row"
        data-testid={s.is_current ? 'device-row-current' : 'device-row'}
      >
        <div className="devices__icon"><Glyph size={20} strokeWidth={1.5} /></div>

        <div className="devices__body">
          {isRenaming ? (
            <div className="devices__rename">
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                maxLength={40}
                className="devices__input"
                aria-label={t.rename}
                data-testid="device-rename-input"
              />
              <button
                onClick={() => rename(s)}
                className="devices__iconbtn"
                aria-label={t.save}
                data-testid="device-rename-save"
              >
                <Check size={16} />
              </button>
              <button
                onClick={() => setRenaming(null)}
                className="devices__iconbtn"
                aria-label="Cancel"
                data-testid="device-rename-cancel"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <>
              <p className="devices__label">
                <span className="devices__name">{s.label}</span>
                {s.is_current && (
                  <>
                    <span className="devices__badge" data-testid="device-current-badge">
                      {t.thisDevice}
                    </span>
                    <span className="devices__badge devices__badge--live"
                          data-testid="device-active-now-badge">
                      {t.activeNow}
                    </span>
                  </>
                )}
              </p>
              <p className="devices__meta">
                {t.signedIn} {t.method[s.auth_method] || s.auth_method}
              </p>
              {(s.last_seen_at || s.created_at) && (
                <p className="devices__meta">
                  {t.lastActive} {when(s.last_seen_at || s.created_at, lang)}
                </p>
              )}
            </>
          )}
        </div>

        {!isRenaming && (
          <div className="devices__actions">
            <button
              onClick={() => { setRenaming(s.session_id); setDraft(s.label); }}
              className="devices__iconbtn"
              aria-label={t.rename}
              data-testid="device-rename-btn"
            >
              <Pencil size={15} />
            </button>
            <button
              onClick={() => end(s)}
              disabled={busy === s.session_id}
              className="devices__end"
              data-testid={s.is_current ? 'device-end-current-btn' : 'device-end-btn'}
            >
              {busy === s.session_id ? t.ending : t.end}
            </button>
          </div>
        )}
      </motion.div>
    );
  };

  return (
    <div className="devices" data-testid="signed-in-devices">
      {error && <p className="devices__error" data-testid="devices-error">{error}</p>}

      {sessions.length === 0 && <p className="devices__sub">{t.empty}</p>}

      {current.length > 0 && (
        <>
          <p className="devices__section" data-testid="devices-section-current">
            {t.currentDevice}
          </p>
          {current.map((s, i) => <Row key={s.session_id} s={s} index={i} />)}
        </>
      )}

      {others.length > 0 && (
        <>
          <div className="devices__sectionrow" data-testid="devices-section-other">
            <p className="devices__section">{t.otherDevices}</p>
            <p className="devices__count" data-testid="devices-other-count">
              {t.devices(others.length)}
            </p>
          </div>
          {others.map((s, i) => <Row key={s.session_id} s={s} index={i} />)}
        </>
      )}

      {others.length > 0 ? (
        <button
          onClick={endOthers}
          disabled={busy === 'others'}
          className="devices__endall"
          data-testid="device-revoke-others-btn"
        >
          <LogOut size={16} />
          <span>{busy === 'others' ? t.ending : t.endOthers}</span>
        </button>
      ) : (
        <p className="devices__sub" data-testid="devices-only-one">{t.only}</p>
      )}

      <div className="devices__note" data-testid="devices-security-note">
        <Lock size={16} strokeWidth={1.6} />
        <p>{t.security}</p>
      </div>

      <style>{`
        .devices { display: flex; flex-direction: column; gap: 10px; }
        .devices__loading { display: flex; justify-content: center; padding: 24px; color: rgba(244,239,231,0.5); }
        .devices__sub { font-size: 13px; line-height: 1.5; color: rgba(244, 239, 231, 0.5); }
        .devices__section {
          font-size: 10.5px; font-weight: 600; letter-spacing: 0.24em; text-transform: uppercase;
          color: rgba(232, 197, 143, 0.82); margin: 12px 0 2px;
        }
        .devices__sectionrow {
          display: flex; align-items: baseline; justify-content: space-between; gap: 12px;
        }
        .devices__count {
          font-size: 10.5px; letter-spacing: 0.2em; color: rgba(244, 239, 231, 0.34);
        }
        .devices__error {
          padding: 10px 12px; border-radius: 12px; font-size: 13px; color: #EF9A9A;
          background: rgba(244, 67, 54, 0.12); border: 1px solid rgba(244, 67, 54, 0.28);
        }
        .devices__row {
          display: flex; align-items: center; gap: 14px; padding: 16px;
          border-radius: 18px; background: rgba(255,255,255,0.028);
          border: 1px solid rgba(244, 239, 231, 0.10);
        }
        .devices__icon {
          flex-shrink: 0; width: 46px; height: 46px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          border: 1px solid rgba(232, 197, 143, 0.42); color: #E8C58F;
        }
        .devices__body { flex: 1; min-width: 0; }
        .devices__label {
          display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 6px;
        }
        .devices__name { font-size: 16px; color: #F4EFE7; }
        .devices__badge {
          font-size: 11px; padding: 3px 10px; border-radius: 999px;
          background: rgba(232, 197, 143, 0.12); border: 1px solid rgba(232, 197, 143, 0.34);
          color: #E8C58F; white-space: nowrap;
        }
        .devices__badge--live {
          background: rgba(94, 200, 184, 0.10); border-color: rgba(94, 200, 184, 0.34);
          color: #5EC8B8;
        }
        .devices__meta {
          font-size: 12.5px; line-height: 1.5; color: rgba(244,239,231,0.44);
        }
        .devices__actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
        .devices__iconbtn {
          width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center;
          justify-content: center; background: transparent;
          border: 1px solid rgba(244, 239, 231, 0.14);
          color: rgba(244,239,231,0.7); cursor: pointer;
          transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
        }
        .devices__iconbtn:hover {
          background: rgba(255,255,255,0.05); color: #F4EFE7;
          border-color: rgba(232, 197, 143, 0.4);
        }
        .devices__end {
          min-height: 44px; padding: 0 14px; border-radius: 12px; font-size: 13px; font-weight: 500;
          background: transparent; border: 1px solid rgba(224, 106, 106, 0.42);
          color: #E06A6A; cursor: pointer; white-space: nowrap;
          transition: background-color 0.2s ease;
        }
        .devices__end:hover { background: rgba(224, 106, 106, 0.10); }
        .devices__end:disabled { opacity: 0.55; cursor: not-allowed; }
        .devices__endall {
          display: flex; align-items: center; justify-content: center; gap: 8px;
          min-height: 48px; margin-top: 8px; border-radius: 999px; font-size: 14px; font-weight: 500;
          background: rgba(255,255,255,0.03); border: 1px solid rgba(244, 239, 231, 0.12);
          color: #F4EFE7; cursor: pointer; transition: background-color 0.2s ease;
        }
        .devices__endall:hover { background: rgba(232,197,143,0.08); border-color: rgba(232,197,143,0.3); }
        .devices__endall:disabled { opacity: 0.55; cursor: not-allowed; }
        .devices__note {
          display: flex; align-items: flex-start; gap: 10px; margin-top: 18px; padding-top: 18px;
          border-top: 1px solid rgba(244, 239, 231, 0.08);
          color: rgba(244, 239, 231, 0.34);
        }
        .devices__note svg { flex-shrink: 0; margin-top: 1px; }
        .devices__note p { font-size: 12.5px; line-height: 1.55; }
        .devices__rename { display: flex; align-items: center; gap: 6px; }
        .devices__input {
          flex: 1; min-width: 0; min-height: 44px; padding: 0 12px; border-radius: 12px;
          background: rgba(0,0,0,0.3); border: 1px solid rgba(232,197,143,0.32);
          color: #F4EFE7; font-size: 14px;
        }
        @media (max-width: 420px) {
          .devices__row { padding: 14px; gap: 10px; }
          .devices__icon { width: 42px; height: 42px; }
          .devices__name { font-size: 15px; }
          .devices__meta { font-size: 12px; }
          .devices__end { padding: 0 11px; font-size: 12.5px; }
        }
      `}</style>
    </div>
  );
};

export default SignedInDevices;
